import React, { useEffect, useState } from 'react';
import { Autocomplete, Checkbox, FormControlLabel, Stack } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { ModalComponent } from 'new/routes/RouterManager';
import { ModalOutProps } from 'new/routes/ModalDefault';
import { User } from 'new/entities/User';
import { getCurrentUser } from 'js/reducers/selectors';
import { addTeam, updateTeamInfo } from 'js/actions';
import {
  ADD_TEAM_REQUEST,
  GET_TEAMS_REQUEST,
  UPDATE_TEAM_INFO_REQUEST,
} from 'js/constants/actionTypes';
import useDidUpdateEffect from 'new/hooks/useDidUpdateEffect';
import { showAlert } from 'new/stores/showAlert/action';
import { useModals } from 'new/hooks/useModals';
import { useOrgMembers } from 'new/hooks/adapters/useOrgMembers';
import { Team } from 'new/entities/Team';
import { omit } from 'lodash';
import { TextField } from 'new/elements/TextField';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';

interface AddTeamProps {
  team: Team;
}

export const AddTeam: ModalComponent = (props: AddTeamProps & ModalOutProps) => {
  const { setModalActions, setLoading, team, setTitle } = props;
  const [members, setMembers] = useState<Array<User>>([]);
  const {
    register,
    watch,
    formState: { errors },
    getValues,
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      name: team?.name || '',
      description: team?.description || '',
    },
  });
  const currentUser = useSelector(getCurrentUser);
  const dispatch = useDispatch<ThunkDispatch<any, any, Action>>();
  const { closeModal } = useModals();
  const users = useOrgMembers();
  const loading = useSelector(
    (state: any) =>
      state.request[ADD_TEAM_REQUEST] ||
      state.request[UPDATE_TEAM_INFO_REQUEST] ||
      state.request[GET_TEAMS_REQUEST],
  );
  const name = watch().name;

  useEffect(() => {
    if (team) {
      setTitle('Edit team');
      const value = team.members.map((member) => {
        return users.find((u) => u.id === member.id);
      });
      setMembers(value);
    }
  }, [team]);

  const actionSuccess = (label) => {
    dispatch(
      showAlert({
        title: `Team ${getValues().name} ${label}.`,
        severity: 'success',
      }),
    );
    closeModal(AddTeam.modalConfig.name);
  };

  const onSubmit = () => {
    const data = getValues();
    if (team) {
      dispatch(
        updateTeamInfo(team.id, {
          ...data,
          members: members.map((member) => ({ email: member.email })),
        }),
      ).then(() => {
        actionSuccess('updated');
      });
      return;
    }
    dispatch(
      addTeam({
        ...data,
        members: members.map((member) => ({ email: member.email })),
      }),
    ).then(() => {
      actionSuccess('created');
    });
  };

  const onChangeMe = () => {
    if (members?.some?.((member) => member.id === currentUser.id)) {
      setMembers([...members?.filter?.((member) => member.id !== currentUser.id)]);
    } else {
      setMembers([...(members || []), users.find((user) => user.id === currentUser.id)]);
    }
  };

  const onChangeMembers = (e, data) => {
    setMembers(data);
  };

  useDidUpdateEffect(() => {
    setLoading(loading);
  }, [loading]);

  useEffect(() => {
    setModalActions({
      ok: {
        text: team ? 'Save' : 'Create',
        onClick: onSubmit,
        disabled: Object.keys(errors || {}).length > 0 || name === '',
      },
    });
  }, [name, errors, team, members]);

  return (
    <Stack flexDirection="column" gap="16px" minWidth="400px">
      <TextField
        label="Name"
        error={!!errors.name}
        helperText={errors.name ? <>{errors.name?.message}</> : undefined}
        {...register('name', {
          required: 'You need to set a name for the team',
        })}
      />
      <TextField label="Description" {...register('description')} />
      <FormControlLabel
        control={
          <Checkbox
            checked={members?.some?.((member) => member?.id === currentUser?.id)}
            onChange={onChangeMe}
          />
        }
        label="Add me to this team"
      />
      <Autocomplete
        {...omit(props, 'setLoading', 'setModalActions', 'setFooter', 'setTitle')}
        multiple
        value={members}
        options={users}
        getOptionLabel={(option: User) => option?.email}
        isOptionEqualToValue={(option, value) => {
          return option?.id === value?.id;
        }}
        filterSelectedOptions
        onChange={onChangeMembers}
        renderInput={(params) => <TextField {...params} label="Users" placeholder="Users" />}
      />
    </Stack>
  );
};

AddTeam.modalConfig = {
  name: 'AddTeam',
  maxWidth: 'md',
  title: 'Add new team',
};
